import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSyllabusScan } from '../../../hooks/useSyllabusScan';
import { syllabusScanService, SCAN_STATUS } from '../../../services/SyllabusScanService';
import { useScanWaitAnalytics } from '../../../hooks/useScanWaitAnalytics';

/* ── SVG Icons ──────────────────────────────────────────────────── */
const Icons = {
  demo: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  dashboard: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  aiTools: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  calendar: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  documents: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  ),
  spinner: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2a10 10 0 0 1 10 10"/></svg>
  ),
};

/* ── Video data ────────────────────────────────────────────────── */
const VIDEOS = [
  {
    id: 'v0',
    title: 'The Full DocuHounds Experience',
    description: 'Watch Scout walk you through everything — from uploading your first syllabus to acing your exams. See how every feature works together to keep you organized, prepared, and ahead of your deadlines all semester long.',
    videoUrl: 'https://www.youtube.com/embed/tkjEytwGtec',
    youtubeId: 'tkjEytwGtec',
    duration: '4 min',
    category: 'Overview',
    playHeadline: 'See the full experience',
    playSub: 'How Scout keeps you organized from day one',
    icon: Icons.demo,
  },
  {
    id: 'v1',
    title: 'Your Personalized Dashboard',
    description: 'Your semester at a glance — upcoming deadlines, study progress, and smart recommendations all in one place. No more juggling tabs or scrambling to find what\'s due next.',
    videoUrl: 'https://www.youtube.com/embed/u6Lp7E7sHgs',
    youtubeId: 'u6Lp7E7sHgs',
    duration: '1 min',
    category: 'Dashboard',
    playHeadline: 'Your semester, one screen',
    playSub: 'Deadlines, progress, and priorities — always visible',
    icon: Icons.dashboard,
  },
  {
    id: 'v2',
    title: 'AI-Powered Study Tools',
    description: 'Flashcards, practice quizzes, audio podcasts, and study guides — all generated instantly from your actual course materials. Study smarter by focusing on what matters most for your next exam.',
    videoUrl: 'https://www.youtube.com/embed/L_I6QFggvKU',
    youtubeId: 'L_I6QFggvKU',
    duration: '2 min',
    category: 'Study Tools',
    playHeadline: 'Study tools built from your materials',
    playSub: 'Flashcards, quizzes, and podcasts — generated in seconds',
    icon: Icons.aiTools,
  },
  {
    id: 'v3',
    title: 'Smart Academic Calendar',
    description: 'Every assignment, exam, and deadline pulled directly from your syllabi and placed on a calendar you can actually rely on. Syncs with Google Calendar so nothing slips through the cracks.',
    videoUrl: 'https://www.youtube.com/embed/k2Dndf1lgXQ',
    youtubeId: 'k2Dndf1lgXQ',
    duration: '1 min',
    category: 'Calendar',
    playHeadline: 'Never miss a deadline',
    playSub: 'Auto-built from your syllabi — syncs to Google Calendar',
    icon: Icons.calendar,
  },
  {
    id: 'v4',
    title: 'ScoutLM',
    description: 'Your AI-powered notebook that turns course materials into interactive study sessions. Upload docs, generate audio overviews, get instant summaries, and chat with your sources — like NotebookLM, built for students.',
    videoUrl: 'https://www.youtube.com/embed/RwHU7EqjkXI',
    youtubeId: 'RwHU7EqjkXI',
    duration: '1 min',
    category: 'AI Notebook',
    playHeadline: 'Your AI-powered notebook',
    playSub: 'Upload, summarize, listen, and chat — all from your materials',
    icon: Icons.documents,
  },
];

/* ── Dynamic processing messages per course ── */
const COURSE_PROCESSING_STAGES = [
  'Reading syllabus…',
  'Extracting assignments…',
  'Finding due dates…',
  'Building schedule…',
];

/* ── Horizontal progress pipeline ──────────────────────────────── */
function ScanPipeline({ scanIds }) {
  const [statuses, setStatuses] = useState({});
  const [stageIdx, setStageIdx] = useState({});

  useEffect(() => {
    const update = () => {
      const next = {};
      for (const id of scanIds) {
        const scan = syllabusScanService.getScan(String(id));
        next[id] = scan?.status || SCAN_STATUS.PENDING;
      }
      setStatuses(next);
    };
    update();
    const unsub = syllabusScanService.on('change', update);
    return unsub;
  }, [scanIds]);

  // Rotate dynamic message per processing course
  useEffect(() => {
    const interval = setInterval(() => {
      setStageIdx(prev => {
        const next = { ...prev };
        for (const id of scanIds) {
          const s = statuses[id];
          if (s !== SCAN_STATUS.COMPLETE && s !== SCAN_STATUS.ERROR) {
            next[id] = ((prev[id] || 0) + 1) % COURSE_PROCESSING_STAGES.length;
          }
        }
        return next;
      });
    }, 2800);
    return () => clearInterval(interval);
  }, [scanIds, statuses]);

  const doneCount = scanIds.filter(id => statuses[id] === SCAN_STATUS.COMPLETE).length;
  const pct = scanIds.length > 0 ? Math.round((doneCount / scanIds.length) * 100) : 0;

  return (
    <div className="sw-pipeline">
      {/* Progress bar */}
      <div className="sw-pipeline-bar">
        <motion.div
          className="sw-pipeline-fill"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>

      {/* Horizontal course nodes */}
      <div className="sw-pipeline-nodes">
        {scanIds.map((id, i) => {
          const status = statuses[id] || SCAN_STATUS.PENDING;
          const done = status === SCAN_STATUS.COMPLETE;
          const err = status === SCAN_STATUS.ERROR;
          const active = !done && !err;
          const stage = COURSE_PROCESSING_STAGES[stageIdx[id] || 0];

          return (
            <motion.div
              key={id}
              className={`sw-pipeline-node${done ? ' sw-pipeline-node--done' : ''}${err ? ' sw-pipeline-node--error' : ''}${active ? ' sw-pipeline-node--active' : ''}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
            >
              <span className="sw-pipeline-icon">
                {done ? Icons.check : <span className="sw-pipeline-spinner">{Icons.spinner}</span>}
              </span>
              <span className="sw-pipeline-label">Course {i + 1}</span>
              {active && (
                <AnimatePresence mode="wait">
                  <motion.span
                    key={stage}
                    className="sw-pipeline-stage"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.2 }}
                  >
                    {stage}
                  </motion.span>
                </AnimatePresence>
              )}
              {done && <span className="sw-pipeline-stage">Ready</span>}
              {err && <span className="sw-pipeline-stage sw-pipeline-stage--error">Error</span>}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Show card (left panel) ─────────────────────────────────────── */
function ShowCard({ video, isActive, onClick }) {
  return (
    <motion.button
      type="button"
      className={`sw-show-card${isActive ? ' sw-show-card--active' : ''}`}
      onClick={onClick}
      whileHover={{ x: 4, scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      animate={isActive ? { scale: 1.02 } : { scale: 1 }}
      transition={{ type: 'spring', stiffness: 380, damping: 24 }}
      aria-pressed={isActive}
    >
      <span className="sw-show-card-icon">{video.icon}</span>
      <div className="sw-show-card-body">
        <span className="sw-show-card-title">{video.title}</span>
        <span className="sw-show-card-dur">{video.duration} &middot; {video.category}</span>
      </div>
    </motion.button>
  );
}

/* ── Floating dust particles ───────────────────────────────────── */
const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  x: `${10 + Math.random() * 80}%`,
  y: `${10 + Math.random() * 80}%`,
  size: 2 + Math.random() * 2.5,
  delay: Math.random() * 4,
  duration: 3 + Math.random() * 3,
}));

function CinemaParticles() {
  return (
    <div className="sw-particles" aria-hidden="true">
      {PARTICLES.map(p => (
        <motion.span
          key={p.id}
          className="sw-particle"
          style={{ left: p.x, top: p.y, width: p.size, height: p.size }}
          animate={{ opacity: [0, 0.6, 0], y: [0, -20, -40] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ── Premium cinematic player ──────────────────────────────────── */
function CinemaPlayer({ video, focused }) {
  const [playing, setPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const cinemaRef = useRef(null);
  const prevVideoId = useRef(video.id);

  useEffect(() => {
    if (video.id !== prevVideoId.current) {
      setPlaying(false);
      setTransitioning(false);
      prevVideoId.current = video.id;
    }
  }, [video.id]);

  // Sync fullscreen state when user exits via Escape key
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!cinemaRef.current) return;
    if (!document.fullscreenElement) {
      cinemaRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const handlePlay = useCallback(() => {
    // Begin curtain-open + Scout fade transition
    setTransitioning(true);
    // After transition, swap to iframe
    setTimeout(() => {
      setPlaying(true);
      setTransitioning(false);
    }, 500);
  }, []);

  const showCurtainOpen = focused || transitioning || playing;

  return (
    <div ref={cinemaRef} className={`sw-cinema${focused ? ' sw-cinema--focused' : ''}${isFullscreen ? ' sw-cinema--fullscreen' : ''}`}>
      {/* Fullscreen toggle — visible on hover */}
      <button
        type="button"
        className="sw-cinema-fs-btn"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
            <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        )}
      </button>

      {/* Ambient glow */}
      <div className="sw-cinema-glow" />

      {/* Spotlight radial behind Scout */}
      <div className="sw-cinema-spotlight" />

      <div className="sw-cinema-inner">
        {/* Top-left label badge */}
        <span className="sw-cinema-label">{video.title}</span>

        {/* Curtain overlays — open on hover/play */}
        <div className={`sw-curtain sw-curtain--left${showCurtainOpen ? ' sw-curtain--open' : ''}`} />
        <div className={`sw-curtain sw-curtain--right${showCurtainOpen ? ' sw-curtain--open' : ''}`} />

        {/* Particles */}
        {!playing && <CinemaParticles />}

        <AnimatePresence mode="wait">
          {playing ? (
            <motion.div
              key={`${video.id}-playing`}
              className="sw-cinema-stage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              <iframe
                className="sw-cinema-iframe"
                src={`${video.videoUrl.replace('www.youtube.com', 'www.youtube-nocookie.com')}?autoplay=1&modestbranding=1&rel=0&showinfo=0&controls=0&iv_load_policy=3&disablekb=0&fs=0&color=white&cc_load_policy=0`}
                title={video.title}
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            </motion.div>
          ) : (
            <motion.div
              key={`${video.id}-poster`}
              className="sw-cinema-stage"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.35 }}
            >
              <button
                type="button"
                className={`sw-cinema-poster${transitioning ? ' sw-cinema-poster--exiting' : ''}`}
                onClick={handlePlay}
                aria-label={`Play ${video.title}`}
              >
                {/* Left atmospheric gradient */}
                <div className="sw-cinema-atmosphere" />

                {/* Ambient background haze */}
                <motion.div
                  className="sw-cinema-haze"
                  animate={{ opacity: [0.3, 0.5, 0.3], scale: [1, 1.05, 1] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Scout spotlight glow (behind Scout) */}
                <div className="sw-cinema-scout-glow" />

                {/* Scout hero image with idle animation */}
                <motion.img
                  className="sw-cinema-scout"
                  src="/scout_images/scout_thumbs_up.png"
                  alt="Scout"
                  draggable="false"
                  animate={transitioning
                    ? { opacity: 0, scale: 0.92, y: 10 }
                    : { scale: [1, 1.04, 1], filter: [
                        'brightness(1.15) contrast(1.1) saturate(1.06) drop-shadow(0 12px 44px rgba(99,102,241,0.5))',
                        'brightness(1.2) contrast(1.1) saturate(1.08) drop-shadow(0 16px 56px rgba(99,102,241,0.65))',
                        'brightness(1.15) contrast(1.1) saturate(1.06) drop-shadow(0 12px 44px rgba(99,102,241,0.5))',
                      ] }
                  }
                  transition={transitioning
                    ? { duration: 0.4, ease: 'easeIn' }
                    : { duration: 3.5, repeat: Infinity, ease: 'easeInOut' }
                  }
                  whileHover={{ x: -2, y: -3 }}
                />

                {/* Gradient scrim */}
                <div className="sw-cinema-scrim" />

                {/* Play overlay */}
                <div className="sw-cinema-play-group">
                  <motion.span
                    className="sw-cinema-play-ring"
                    animate={{
                      boxShadow: [
                        '0 0 32px rgba(99,102,241,0.28), 0 0 64px rgba(99,102,241,0.1)',
                        '0 0 56px rgba(99,102,241,0.5), 0 0 96px rgba(99,102,241,0.18)',
                        '0 0 32px rgba(99,102,241,0.28), 0 0 64px rgba(99,102,241,0.1)',
                      ],
                      scale: [1, 1.06, 1],
                    }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="6 3 20 12 6 21 6 3" />
                    </svg>
                  </motion.span>
                  <span className="sw-cinema-play-headline">{video.playHeadline}</span>
                  <span className="sw-cinema-play-sub">{video.playSub}</span>
                </div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── Error panel ───────────────────────────────────────────────── */
function ScanErrorPanel({ error, onManual }) {
  return (
    <motion.div
      className="sw-hero sw-hero--error"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <span className="sw-pill sw-pill--error">⚠ Scan error</span>
      <h2 className="sw-headline">Something went wrong</h2>
      <p className="sw-supporting">
        {(typeof error === 'string' ? error : null) || "We couldn't process your syllabus, but you can still set up your course manually."}
      </p>
      <button className="sw-cta sw-cta--secondary" onClick={onManual}>
        Set up course manually →
      </button>
    </motion.div>
  );
}

/* ── Main step ─────────────────────────────────────────────────── */

/**
 * Step 4 — Dual-state Scan Wait
 *
 * STATE A: Processing — "Building your semester" with progress + video section
 * STATE B: Completed  — "Your semester is ready" with primary CTA + de-emphasized videos
 */
export default function ScanWaitStep({ data, updateData, goNext, goBack, goToStep }) {
  const [selectedVideo, setSelectedVideo] = useState(VIDEOS[0]);
  const [messageIdx, setMessageIdx] = useState(0);
  const [completedGlow, setCompletedGlow] = useState(false);
  const [cinemaFocused, setCinemaFocused] = useState(false);
  const completedGlowTimer = useRef(null);

  const scanIds = useMemo(() => data.scanIds || [], [data.scanIds]);
  const primaryScanId = scanIds[0] || data.scanId || null;

  const { result, error, isProcessing, isComplete, isError, startScan } =
    useSyllabusScan(primaryScanId);

  // Analytics
  const { handleVideoSelect, handleReviewClick, handleSkipScan, handleManualFallback, handleBack } =
    useScanWaitAnalytics({
      scanIds, primaryScanId, result, error, isProcessing, isComplete, isError,
      selectedVideo, setSelectedVideo, goNext, goBack, goToStep,
    });

  // Legacy fallback
  useEffect(() => {
    if (data.syllabusDocumentId && !primaryScanId) {
      const newId = startScan(data.syllabusDocumentId, {
        courseName: data.courseName || undefined,
      });
      updateData({ scanId: newId, scanIds: [newId, ...scanIds.slice(1)] });
    }
  }, [data.syllabusDocumentId, primaryScanId, data.courseName, startScan, updateData, scanIds]);

  // Background scan tracker — also detects scans stuck in PROCESSING beyond
  // the timeout so the user is never blocked indefinitely.
  const STALE_THRESHOLD = 3 * 60 * 1000; // match _executeScan timeout (3 min)
  const [bgScanSummary, setBgScanSummary] = useState({ total: 0, done: 0, errors: 0, stale: 0 });

  useEffect(() => {
    const update = () => {
      let done = 0, errors = 0, stale = 0;
      const now = Date.now();
      for (const id of scanIds) {
        const scan = syllabusScanService.getScan(String(id));
        if (scan?.status === SCAN_STATUS.COMPLETE) done++;
        else if (scan?.status === SCAN_STATUS.ERROR) errors++;
        else if (scan?.startedAt && (now - scan.startedAt) > STALE_THRESHOLD) stale++;
      }
      setBgScanSummary({ total: scanIds.length || 1, done, errors, stale });
    };
    update();
    const unsub = syllabusScanService.on('change', update);
    // Poll every 30 s so we catch scans that go stale (they never emit 'change')
    const interval = setInterval(update, 30000);
    return () => { unsub(); clearInterval(interval); };
  }, [scanIds, STALE_THRESHOLD]);

  // Rotate processing messages
  const SCAN_MESSAGES = useMemo(() => [
    'Reading your syllabi…',
    'Extracting course details…',
    'Finding assignments and due dates…',
    'Building your schedules…',
    'Almost there…',
  ], []);

  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setMessageIdx(prev => (prev + 1) % SCAN_MESSAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isProcessing, SCAN_MESSAGES]);

  // Persist primary scan result
  useEffect(() => {
    if (isComplete && result) {
      updateData({ scanResult: result });
    }
  }, [isComplete, result, updateData]);

  // Collect background results — sweep already-complete scans on mount, then
  // subscribe for future completions.  Uses functional updateData to avoid
  // stale-closure overwrites when multiple scans complete before re-render.
  useEffect(() => {
    if (scanIds.length <= 1) return;
    const collected = new Set();
    // Seed with any already-persisted results so we never double-add
    if (Array.isArray(data.scanResults)) {
      data.scanResults.forEach(r => r._scanId != null && collected.add(String(r._scanId)));
    }

    // Sweep for scans that completed BEFORE this effect subscribed (missed events)
    const alreadyDone = [];
    for (const id of scanIds) {
      const key = String(id);
      if (collected.has(key)) continue;
      const scan = syllabusScanService.getScan(key);
      if (scan?.status === SCAN_STATUS.COMPLETE && scan.result) {
        collected.add(key);
        alreadyDone.push({ ...scan.result, _scanId: key });
      }
    }
    if (alreadyDone.length > 0) {
      updateData(prev => ({
        scanResults: [...(prev.scanResults || []), ...alreadyDone],
      }));
    }

    // Subscribe for any scans that haven't finished yet
    const unsub = syllabusScanService.on('complete', (scanId, scanResult) => {
      const key = String(scanId);
      if (collected.has(key)) return;
      collected.add(key);
      updateData(prev => ({
        scanResults: [...(prev.scanResults || []), { ...scanResult, _scanId: scanId }],
      }));
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanIds, updateData]);

  // Completion glow pulse
  useEffect(() => {
    if (isComplete && !completedGlow) {
      setCompletedGlow(true);
      completedGlowTimer.current = setTimeout(() => setCompletedGlow(false), 2000);
    }
    return () => clearTimeout(completedGlowTimer.current);
  }, [isComplete, completedGlow]);

  const totalScans = bgScanSummary.total || 1;
  const settledCount = bgScanSummary.done + bgScanSummary.errors + bgScanSummary.stale;
  const failedCount = bgScanSummary.errors + bgScanSummary.stale;
  const allDone = isComplete && settledCount >= totalScans;

  return (
    <div className={`onboarding-card sw-card${allDone ? ' sw-card--complete' : ''}${completedGlow ? ' sw-card--glow' : ''}`}>

      {/* ── Hero section (state-driven) ── */}
      <AnimatePresence mode="wait">
        {isError ? (
          <ScanErrorPanel key="error" error={error} onManual={handleManualFallback} />
        ) : allDone ? (
          /* ── STATE B: Completed ── */
          <motion.div
            key="complete"
            className="sw-hero sw-hero--complete"
            initial={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <motion.span
              className="sw-pill sw-pill--complete"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 300 }}
            >
              {Icons.check}
              {failedCount > 0
                ? `${bgScanSummary.done} of ${totalScans} syllabi processed`
                : 'All courses processed'}
            </motion.span>

            <h1 className="sw-headline sw-headline--complete">
              {failedCount > 0 ? 'Almost there' : 'Your semester is ready'}
            </h1>
            <p className="sw-supporting">
              {failedCount > 0
                ? `${failedCount} syllab${failedCount === 1 ? 'us' : 'i'} couldn\u2019t be read \u2014 you can add those details manually.`
                : 'Everything is organized and ready to go.'}
            </p>

            <motion.button
              className="sw-cta sw-cta--primary"
              onClick={handleReviewClick}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Review your courses →
            </motion.button>
          </motion.div>
        ) : (
          /* ── STATE A: Processing ── */
          <motion.div
            key="processing"
            className="sw-hero sw-hero--processing"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
            transition={{ duration: 0.4 }}
          >
            <span className="sw-pill sw-pill--processing">
              <span className="sw-pill-dot" />
              Building your semester
            </span>

            <h1 className="sw-headline">Building your semester</h1>
            <p className="sw-supporting">
              Reading your syllabi and organizing everything for you.
            </p>

            {/* Progress indicator */}
            {totalScans > 1 ? (
              <ScanPipeline scanIds={scanIds} />
            ) : (
              <div className="sw-processing-anim">
                <span className="sw-processing-dots">
                  <span /><span /><span />
                </span>
                <span className="sw-processing-msg">{SCAN_MESSAGES[messageIdx]}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Divider ── */}
      <div className="sw-divider" />

      {/* ── Video section — left rail + cinema player ── */}
      <div
        className={`sw-theater${allDone ? ' sw-theater--muted' : ''}${cinemaFocused ? ' sw-theater--focused' : ''}`}
      >
        <div className="sw-theater-header">
          <h3 className="sw-theater-title">
            {allDone ? 'Want a quick walkthrough?' : 'While we set things up, let Scout show you around'}
          </h3>
          {!allDone && (
            <p className="sw-theater-subtitle">Pick a topic below — Scout will walk you through it.</p>
          )}
        </div>

        <div className="sw-theater-layout">
          {/* Left panel — show cards */}
          <nav className="sw-show-panel" aria-label="Video walkthroughs">
            <h4 className="sw-show-panel-header">What do you want to see?</h4>
            {VIDEOS.map(video => (
              <ShowCard
                key={video.id}
                video={video}
                isActive={selectedVideo.id === video.id}
                onClick={() => handleVideoSelect(video)}
              />
            ))}
          </nav>

          {/* Right — featured cinema player */}
          <div
            className="sw-featured"
            onMouseEnter={() => setCinemaFocused(true)}
            onMouseLeave={() => setCinemaFocused(false)}
          >
            <CinemaPlayer video={selectedVideo} focused={cinemaFocused} />

            {/* Now Playing info */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedVideo.id}
                className="sw-now-playing"
                initial={{ opacity: 0, y: 8, filter: 'blur(2px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -6, filter: 'blur(2px)' }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <div className="sw-now-playing-content">
                  <div className="sw-now-playing-header">
                    <span className="sw-now-playing-label">Now Playing</span>
                  </div>
                  <span className="sw-now-playing-title">{selectedVideo.title}</span>
                  <span className="sw-now-playing-desc">{selectedVideo.description}</span>
                </div>
                <div className="sw-now-playing-aside">
                  <span className="sw-now-playing-meta-item">{selectedVideo.duration}</span>
                  <span className="sw-now-playing-meta-item">{selectedVideo.category}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="onboarding-actions sw-footer">
        <button className="onboarding-btn secondary" onClick={handleBack}>
          ← Back
        </button>
      </div>
    </div>
  );
}
